'use client';

import {
    clearStoredAuthTokens, getStoredAuthToken, readBrowserSession, sessionSnapshot, replaceRefreshedSession,
} from './auth-storage';
import { tokenIdentity } from './browser-session';

type RequestHeaders = HeadersInit | Record<string, string | null | undefined>;

type ApiRequestConfig = {
    headers?: RequestHeaders;
    signal?: AbortSignal;
    timeout?: number;
    authRetried?: boolean;
};

type ApiResponse<T> = {
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
};

class ApiError<T = unknown> extends Error {
    response?: ApiResponse<T>;

    constructor(message: string, response?: ApiResponse<T>) {
        super(message);
        this.name = 'ApiError';
        this.response = response;
    }
}

function getRuntimeConfig() {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.__APP_CONFIG__ ?? null;
}

const defaults = {
    baseURL: '',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
};

function getApiKey() {
    return getRuntimeConfig()?.apiKey?.trim() || null;
}

function setHeader(headers: Headers, name: string, value: string | null) {
    if (value) {
        headers.set(name, value);
        return;
    }

    headers.delete(name);
}

function applyHeaders(target: Headers, source?: RequestHeaders) {
    if (!source) {
        return;
    }

    if (source instanceof Headers) {
        source.forEach((value, key) => {
            target.set(key, value);
        });
        return;
    }

    if (Array.isArray(source)) {
        source.forEach(([key, value]) => {
            if (value == null) {
                target.delete(key);
                return;
            }

            target.set(key, value);
        });
        return;
    }

    Object.entries(source).forEach(([key, value]) => {
        if (value == null) {
            target.delete(key);
            return;
        }

        target.set(key, value);
    });
}

function getRequestHeaders(config: ApiRequestConfig | undefined, body: BodyInit | undefined) {
    const headers = new Headers();
    applyHeaders(headers, defaults.headers);

    setHeader(headers, 'apikey', getApiKey());

    const token = getStoredAuthToken();
    setHeader(headers, 'Authorization', token ? `Bearer ${token}` : null);
    applyHeaders(headers, config?.headers);

    if (body instanceof FormData) {
        headers.delete('Content-Type');
    }

    return headers;
}

function getBaseUrl() {
    return defaults.baseURL || getRuntimeConfig()?.apiUrl || '';
}

function resolveUrl(url: string) {
    if (/^https?:\/\//i.test(url)) {
        return url;
    }

    const baseUrl = getBaseUrl();
    if (!baseUrl) {
        return url;
    }

    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;

    return new URL(normalizedUrl, normalizedBaseUrl).toString();
}

function getRequestBody(body: unknown, headers: Headers): BodyInit | undefined {
    if (body == null) {
        return undefined;
    }

    if (
        body instanceof FormData ||
        body instanceof URLSearchParams ||
        body instanceof Blob ||
        body instanceof ArrayBuffer ||
        typeof body === 'string'
    ) {
        return body;
    }

    if (ArrayBuffer.isView(body)) {
        return body as unknown as BodyInit;
    }

    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return JSON.stringify(body);
}

async function parseResponseBody(response: Response) {
    const text = await response.text();
    if (!text) {
        return null;
    }

    const trimmed = text.trim();
    const contentType = response.headers.get('Content-Type') ?? '';
    const isJson =
        contentType.includes('application/json') ||
        contentType.includes('+json') ||
        trimmed.startsWith('{') ||
        trimmed.startsWith('[');

    if (!isJson) {
        return text;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

function linkAbortSignal(controller: AbortController, signal?: AbortSignal) {
    if (!signal) {
        return () => undefined;
    }

    if (signal.aborted) {
        controller.abort(signal.reason);
        return () => undefined;
    }

    const abortHandler = () => controller.abort(signal.reason);
    signal.addEventListener('abort', abortHandler, { once: true });
    return () => signal.removeEventListener('abort', abortHandler);
}

let refreshing: { loginId: string; promise: Promise<void> } | null = null;
async function refreshSession() {
    const session = readBrowserSession();
    if (!session) throw new Error('Sign in to continue.');
    if (refreshing?.loginId === session.loginId) return refreshing.promise;
    const expected = sessionSnapshot();
    const promise = (async () => {
        const response = await fetch(resolveUrl('/User/refresh-token'), {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json', apikey: getApiKey() ?? '', 'X-PteUltra-Acquisition': '1' },
            body: JSON.stringify({ refreshToken: session.refreshToken }), signal: AbortSignal.timeout(15000),
        });
        const payload = await response.json() as { success?: boolean; data?: { accessToken?: string; refreshToken?: string } };
        if (readBrowserSession()?.loginId !== session.loginId) throw new DOMException('Login changed.', 'AbortError');
        if (response.status >= 500 || response.status === 429 || response.status === 408) throw new Error('Unable to check your login. Please try again.');
        if (!response.ok || payload.success !== true || !payload.data?.accessToken || !payload.data?.refreshToken) {
            // A competing Home/App refresh may have consumed this token while
            // its response is still travelling. Both clients cancel refresh
            // after 15s. Wait for that writer before declaring rejection final.
            const deadline = Date.now() + 16000;
            while (sessionSnapshot() === expected && Date.now() < deadline) {
                await new Promise(resolve => window.setTimeout(resolve, 100));
            }
            if (readBrowserSession()?.loginId !== session.loginId) throw new DOMException('Login changed.', 'AbortError');
            if (sessionSnapshot() !== expected) return;
            clearStoredAuthTokens({ expectedSnapshot: expected });
            throw new Error('Sign in to continue.');
        }
        if (!replaceRefreshedSession(expected, payload.data.accessToken, payload.data.refreshToken)
            && sessionSnapshot() === expected) {
            clearStoredAuthTokens({ expectedSnapshot: expected });
            throw new Error('Sign in to continue.');
        }
    })();
    refreshing = { loginId: session.loginId, promise };
    try { await promise; } finally { if (refreshing?.promise === promise) refreshing = null; }
}

async function request<T>(method: string, url: string, body?: unknown, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    const initialSession = readBrowserSession();
    const probe = new Headers({ Authorization: 'present' });
    applyHeaders(probe, config?.headers);
    const explicitlyAnonymous = !probe.get('Authorization');
    if (initialSession && !explicitlyAnonymous && (tokenIdentity(initialSession.accessToken)?.expires ?? 0) <= Date.now() + 5000) await refreshSession();
    if (readBrowserSession()?.loginId !== initialSession?.loginId) throw new DOMException('Login changed.', 'AbortError');
    const expected = sessionSnapshot();
    const controller = new AbortController();
    const unlinkAbortSignal = linkAbortSignal(controller, config?.signal);
    const headers = getRequestHeaders(config, body instanceof FormData ? body : undefined);
    const ownApi = new URL(resolveUrl(url), window.location.href).origin === new URL(getBaseUrl(), window.location.href).origin;
    if (ownApi) headers.set('X-PteUltra-Acquisition', '1');
    const requestBody = getRequestBody(body, headers);
    const timeout = window.setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), config?.timeout ?? defaults.timeout);

    try {
        const response = await fetch(resolveUrl(url), {
            method,
            headers,
            body: requestBody,
            signal: controller.signal,
            credentials: ownApi ? 'include' : 'omit',
        });

        const data = await parseResponseBody(response);
        if (readBrowserSession()?.loginId !== initialSession?.loginId) throw new DOMException('Login changed.', 'AbortError');
        const apiResponse: ApiResponse<T> = {
            data: data as T,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
        };

        if (response.status === 401 && initialSession && !explicitlyAnonymous) {
            if (!config?.authRetried) {
                if (sessionSnapshot() === expected) await refreshSession();
                if (readBrowserSession()?.loginId !== initialSession.loginId) throw new DOMException('Login changed.', 'AbortError');
                return request<T>(method, url, body, { ...config, authRetried: true });
            }
            clearStoredAuthTokens({ expectedSnapshot: expected });
        }

        if (!response.ok) {
            throw new ApiError(response.statusText || `Request failed with status ${response.status}`, apiResponse);
        }

        return apiResponse;
    } finally {
        unlinkAbortSignal();
        clearTimeout(timeout);
    }
}

const api = {
    defaults,
    get<T>(url: string, config?: ApiRequestConfig) {
        return request<T>('GET', url, undefined, config);
    },
    post<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('POST', url, body, config);
    },
    put<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('PUT', url, body, config);
    },
    patch<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('PATCH', url, body, config);
    },
    delete<T>(url: string, config?: ApiRequestConfig) {
        return request<T>('DELETE', url, undefined, config);
    }
};

export default api;
